// ── ComposeClaudePanel (Evo Brain — panel 6b: composer) ──────────────────────
// Reemplaza compose-chat + quick-action (bespoke, stateless, sin tools, sin
// persistencia — ver napkin). Mismo dock que el resto de la app: si el
// destinatario matchea un contacto existente, el módulo activo pasa a ser
// 'contact' (gana read_contact_profile, draft_email_to_contact, etc. — ver
// tools/contacts.ts); si no, queda en 'email_message' sin entidad. En ambos
// casos se manda composeContext (destinatario/asunto/cuerpo EN CURSO, sin
// guardar todavía) para que el dock sepa qué se está redactando ahora mismo
// — ese estado vive solo en el form del browser, nunca llega a la DB hasta
// guardarse.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/Button.js';
import { extractEmailAddress } from '../../utils/email.js';
import { useAssistantModule, useRegisterAssistantActive, type ComposeContext } from '../../contexts/AssistantContext.js';
import { useContactByEmail } from '../../hooks/useContacts.js';

const QUICK_ACTION_PROMPTS = {
  draft_reply: 'Redacta un email profesional en español para este destinatario. Si hay perfil o historial del contacto, úsalo para personalizarlo. Si ya hay un borrador en curso, mejoralo — no repitas lo mismo. Devuelve el texto en el chat, no lo guardes ni lo envíes.',
  summarize_recent: 'Resume en 3-5 bullets concisos las últimas interacciones con este contacto. Identificá el estado actual de la relación y si hay alguna acción pendiente.',
  suggest_tone: 'Analiza el perfil e historial de este contacto. Sugerime el tono adecuado para este email (formal o casual, breve o detallado, urgente o relajado) y por qué.',
} as const;

interface Props {
  recipient: string;
  draft?: { subject?: string; body?: string } | undefined;
}

export function ComposeClaudePanel({ recipient, draft }: Props) {
  const { t } = useTranslation();
  const [debouncedEmail, setDebouncedEmail] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const cleanEmail = extractEmailAddress(recipient);
      setDebouncedEmail(cleanEmail.includes('@') && cleanEmail.includes('.') ? cleanEmail : '');
    }, 500);
    return () => clearTimeout(timer);
  }, [recipient]);

  const { data: contactData, isFetching: contactFetching } = useContactByEmail(debouncedEmail);
  const contact = contactData?.contact ?? null;
  const profile = contactData?.profile ?? null;
  const contactId = contact?.id ?? null;
  const hasProfile = !!profile?.markdownContent;
  const contactName = contact
    ? ([contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || undefined)
    : undefined;

  const isValidEmail = recipient.includes('@') && recipient.includes('.');

  // Sin contacto resuelto: módulo activo queda en email_message/null (sin
  // tools de entidad). Apenas matchea un contacto, salta a 'contact'.
  const entityType = contactId != null ? 'contact' : 'email_message';
  const entityId = contactId != null ? String(contactId) : null;

  const assistant = useAssistantModule(entityType, entityId, undefined);
  const isStreaming = assistant.streaming.phase !== 'idle';

  const composeContext: ComposeContext = {
    ...(isValidEmail ? { recipientEmail: debouncedEmail || extractEmailAddress(recipient) } : {}),
    ...(contactName ? { contactName } : {}),
    ...(draft?.subject ? { subject: draft.subject } : {}),
    ...(draft?.body ? { body: draft.body } : {}),
  };

  const headerText = !isValidEmail
    ? t('email.compose.context.no_recipient')
    : contactFetching
    ? t('common.loading')
    : contact
    ? (hasProfile ? t('email.compose.context.with_profile', { name: contactName }) : t('email.compose.context.without_profile', { name: contactName }))
    : t('email.compose.context.no_contact');

  const topContent = (
    <div className="px-3 pt-3 space-y-2">
      <div className="text-xs font-medium" style={{ color: 'var(--evo-fg-2)' }}>{headerText}</div>

      {!isValidEmail && (
        <div className="rounded p-3 text-xs" style={{ background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)' }}>
          {t('email.compose.context.no_recipient_hint')}
        </div>
      )}

      {isValidEmail && !contactFetching && !contact && (
        <div className="rounded p-3 text-xs" style={{ background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)' }}>
          {t('email.compose.context.no_history')}
        </div>
      )}

      {isValidEmail && !contactFetching && contact && (
        <>
          {hasProfile && profile?.markdownContent && (
            <div className="rounded p-2.5 text-xs" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', lineHeight: 1.5, maxHeight: '160px', overflowY: 'auto' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="mb-1 last:mb-0" style={{ color: 'var(--evo-fg-2)' }}>{children}</p>,
                  ul: ({ children }) => <ul className="list-disc ml-3 mb-1" style={{ color: 'var(--evo-fg-2)' }}>{children}</ul>,
                  li: ({ children }) => <li className="mb-0.5">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                }}>
                {profile.markdownContent.slice(0, 600)}
              </ReactMarkdown>
            </div>
          )}

          {!hasProfile && (
            <div className="rounded p-2.5 text-xs" style={{ background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)' }}>
              {t('email.compose.context.profile_inactive')}
            </div>
          )}

          {hasProfile && (
            <div className="flex flex-col gap-1 mt-1">
              {(Object.keys(QUICK_ACTION_PROMPTS) as Array<keyof typeof QUICK_ACTION_PROMPTS>).map(action => (
                <Button key={action}
                  variant="outline"
                  tone="cyan"
                  size="sm"
                  disabled={isStreaming}
                  onClick={() => { void assistant.sendMessage(QUICK_ACTION_PROMPTS[action], composeContext); }}
                >
                  {t(`email.compose.quick.${action === 'draft_reply' ? 'draft_reply' : action === 'summarize_recent' ? 'summarize' : 'suggest_tone'}`)}
                </Button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  useRegisterAssistantActive(entityType, entityId, {
    title: t('email.assistant.title'),
    placeholder: t('email.assistant.placeholder'),
    inputDisabled: false,
    emptyHint: t('email.assistant.hint'),
    entityLink: null,
    topContent,
    composeContext,
  });

  return null;
}
